import { useCallback, useState } from 'react';

import { env } from '@/env';
import { useHaptics } from '@/hooks/useHaptics';
import { useStartMatch } from '@/hooks/useStartMatch';
import { qgammonAuthHeaders } from '@/lib/qgammon/auth';
import { corpoPartitaBot, type SceltePartitaBot } from '@/lib/qgammon/partitaBot';

/** Perche' la partita col bot non e' partita. */
export type ErrorePartitaBot =
  /** `409 too_many_active_matches`: il tetto delle partite aperte (`max` se il server lo dice). */
  | { kind: 'atMatchCap'; max: number | null }
  /** `404 bot_not_found`: il bot di quel livello non esiste su questo server. */
  | { kind: 'livelloAssente' }
  /**
   * `503` alla creazione col livello sperimentale (`candidate_bot_unavailable`, e il 503 per un'identita' del candidato che
   * non torna): il bot sperimentale non e' disponibile. NESSUN ripiego su un altro bot: l'utente lo legge e sceglie.
   */
  | { kind: 'sperimentaleNonDisponibile' }
  /** Tutto il resto (rete, 5xx, 4xx senza nome). */
  | { kind: 'fallita' };

export interface PartitaControBot {
  avvio: boolean;
  errore: ErrorePartitaBot | null;
  gioca: (scelte: SceltePartitaBot) => void;
}

/**
 * Crea una partita di pratica contro il bot (`POST /matches/practice`) e ci porta il giocatore.
 *
 * ⚠️ **DUPLICATO, dichiarato**: e' la stessa logica di `BackgammonLobby.playVsBot` (la scheda «Gioca»),
 * con le scelte del foglio del bot al posto del bottone nudo. La lobby NON si tocca in questo giro
 * (contratto del fronte `ranked-bg`: «Gioca» e' fuori perimetro), quindi il codice esiste due volte
 * finche' qualcuno non fa passare anche la lobby da qui. Le regole sono le stesse, e vanno tenute
 * uguali:
 *  - auth come la coda (`qgammonAuthHeaders(true)`: Bearer Logto, o dev-bypass in dev);
 *  - `409` → il tetto delle partite aperte, col `max` se c'e' — la pratica e' il modo piu' economico
 *    di aprire sessioni, e il server le limita;
 *  - ⚠️ `startMatch` puo' NON far partire niente (un'altra partita attiva chiede conferma, e su un
 *    rifiuto rende `false`): allora il bottone torna premibile, invece di restare su «Avvio…» per
 *    sempre con una partita orfana appena creata (misurato su stg-tma l'11/09).
 * In piu' della lobby: `404 bot_not_found` ha un avviso suo (il livello Facile e' un bot nuovo).
 */
export function usePartitaControBot(): PartitaControBot {
  const haptics = useHaptics();
  const startMatch = useStartMatch();
  const [avvio, setAvvio] = useState(false);
  const [errore, setErrore] = useState<ErrorePartitaBot | null>(null);

  const gioca = useCallback(
    (scelte: SceltePartitaBot) => {
      setAvvio(true);
      setErrore(null);
      haptics.impact('medium');
      void (async () => {
        try {
          const headers = await qgammonAuthHeaders(true);
          const res = await fetch(`${env.qgammonWsUrl}/matches/practice`, {
            method: 'POST',
            headers,
            body: JSON.stringify(corpoPartitaBot(scelte)),
          });
          if (res.status === 503 && scelte.livello === 'sperimentale') {
            haptics.notification('error');
            setErrore({ kind: 'sperimentaleNonDisponibile' });
            setAvvio(false);
            return;
          }
          if (res.status === 409 || res.status === 404) {
            const body = (await res.json().catch(() => ({}))) as { error?: string; max?: number };
            haptics.notification('error');
            setErrore(
              res.status === 409 && body.error === 'too_many_active_matches'
                ? { kind: 'atMatchCap', max: body.max ?? null }
                : res.status === 404 && body.error === 'bot_not_found'
                  ? { kind: 'livelloAssente' }
                  : { kind: 'fallita' },
            );
            setAvvio(false);
            return;
          }
          if (!res.ok) throw new Error(`practice create failed: ${res.status}`);
          const match = (await res.json()) as { matchId: string; humanColor: 'W' | 'B' };
          const partito = startMatch({
            game: 'qgammon',
            matchId: match.matchId,
            perspective: match.humanColor,
            vsBot: true,
          });
          if (!partito) setAvvio(false);
        } catch {
          haptics.notification('error');
          setErrore({ kind: 'fallita' });
          setAvvio(false);
        }
      })();
    },
    [haptics, startMatch],
  );

  return { avvio, errore, gioca };
}
